'use strict';

import Widget from 'widgets/Widget';

var loadMoreButton = document.querySelector('.bv-content-btn-pages-load-more');

/**
 * @description Base Reviews implementation
 */
export default class Reviews extends Widget {
    prefs() {
        return {
            ...super.prefs()
        };
    }

    init() {
        this.waitForBVRRContainer(() => {
            this.observeBVRRContainer();
            this.setupModal();
            this.readMoreHandler();
            this.copyReviewsChartComputedStyles();
            this.reviewsChartClone();
            this.filterModifier();
            this.copyComputedStyles();
            this.chartRateNumberModifier();
            this.highlightedReviewRatingInjection();
            this.noReviewsTextInjection();
            this.updateActionBarHeader();
            this.individualRatingNumber();
            this.cloneReviewHeaderMeta();
            this.updateHighlightReviewDescription();
            this.reviewHighlightCountModifier();
            this.cloneReviewContentData();
            this.removeMicrodataAttributes();

            if (loadMoreButton) {
                this.handleLoadMore();
            }
        });

        window.addEventListener('resize', this.adjustModalHeight.bind(this));
    }

    waitForBVRRContainer(callback) {
        const maxAttempts = 50;
        let attempts = 0;

        const interval = setInterval(() => {
            const container = document.getElementById('BVRRContainer');
            if (container) {
                callback();
                this.removeMicrodataAttributesFromElement(container);
                clearInterval(interval);
            } else if (attempts >= maxAttempts) {
                window.console.warn('BVRRContainer not found.');
                clearInterval(interval);
            }
            attempts++;
        }, 100);
    }

    setupModal() {
        this.adjustModalHeight();

        var modalContainer = document.querySelector('.bv-reviews-container-modal');
        var modalBackground = document.querySelector('.modal-background');

        if (modalContainer) {
            var observer = new MutationObserver(this.handleModalVisibility.bind(this));
            observer.observe(modalContainer, { attributes: true, attributeFilter: ['class'] });
        }

        if (modalBackground) {
            modalBackground.addEventListener('click', () => {
                this.closeReviewsModal();
            });
        }
    }

    observeBVRRContainer() {
        const targetNode = document.getElementById('BVRRContainer');
        if (targetNode) {
            const observer = new MutationObserver((mutations, obs) => {
                obs.disconnect();
                setTimeout(() => {
                    this.updateProductQuestionsText();
                    this.paginationModifier();
                    this.reviewAuthorModifier();
                    this.updateRatings();
                    obs.observe(targetNode, { childList: true, subtree: true });
                }, 200);
            });
            observer.observe(targetNode, { childList: true, subtree: true });
        } else {
            console.warn('Element #BVRRContainer not found.');
        }
    }

    updateProductQuestionsText() {
        const questionContainers = document.querySelectorAll('.bv-content-product-questions');

        if (!questionContainers.length) {
            return;
        }

        questionContainers.forEach(container => {
            let sizePurchased = '';
            let usualSize = '';
            let colour = '';

            const labels = container.querySelectorAll('.bv-content-data-label');
            const values = container.querySelectorAll('.bv-content-data-value');

            labels.forEach((label, index) => {
                const text = label.textContent.trim();
                if (text.includes('Size purchased')) {
                    sizePurchased = values[index]?.textContent.trim();
                } else if (text.includes('Usual size')) {
                    usualSize = values[index]?.textContent.trim();
                } else if (text.includes('Colour')) {
                    colour = values[index]?.textContent.trim().toLowerCase();
                }
            });

            if (sizePurchased && usualSize) {
                const newText = colour
                    ? `Bought in ${colour} in size ${sizePurchased}, usually wears ${usualSize}`
                    : `Bought size ${sizePurchased}, usually wears ${usualSize}`;

                container.innerHTML = `<span class="bv-content-product-questions-text">${newText}</span>`;
            }
        });
    }

    paginationModifier() {
        var paginationSpan = document.querySelector('.bv-content-pagination-pages-current span');
        var reviewListContainer = document.querySelector('.bv-content-list.bv-content-list-reviews');

        if (paginationSpan && reviewListContainer) {
            var textContent = paginationSpan.textContent.trim();
            var parts = textContent.split('–');
            var newText = '';

            if (parts.length > 1) {
                newText = 'Showing ' + parts[1].trim();
            } else {
                newText = 'Showing ' + textContent;
            }

            var outerDiv = document.querySelector('.custom-review-wrapper');
            if (!outerDiv) {
                outerDiv = document.createElement('div');
                outerDiv.className = 'custom-review-wrapper';

                var reviewTextSpan = document.createElement('span');
                reviewTextSpan.className = 'custom-review-text';
                outerDiv.appendChild(reviewTextSpan);
                reviewListContainer.parentNode.insertBefore(outerDiv, reviewListContainer.nextSibling);
            }

            var reviewTextSpan = document.querySelector('.custom-review-text');
            if (reviewTextSpan) {
                reviewTextSpan.textContent = newText;
            }
        }
    }

    highlightedReviewRatingInjection() {
        const reviewList = document.querySelector('.bv-content-list.bv-content-list-reviews');
        const firstReview = reviewList?.querySelector('li.bv-content-item');
        const ratingElement = firstReview?.querySelector('.bv-content-rating.bv-rating-ratio');

        const clonedRating = ratingElement?.cloneNode(true);
        if (clonedRating) {
            this.copyComputedStyles(ratingElement, clonedRating);
        }

        const highlightRatingContainer = document.querySelector('.highlight-rating-summary-injected');
        if (highlightRatingContainer) {
            const highlightStarsContainer = highlightRatingContainer.querySelector('.highlight-rating-stars');
            if (highlightStarsContainer) {
                highlightStarsContainer.appendChild(clonedRating);

                this.removeMicrodataAttributesFromElement(highlightRatingContainer);
            }
        }
    }

    removeMicrodataAttributesFromElement(element) {
        if (!element) {
            return;
        }

        const elements = element.querySelectorAll('[itemprop], [itemscope], [itemtype]');
        elements.forEach(el => {
            el.removeAttribute('itemprop');
            el.removeAttribute('itemscope');
            el.removeAttribute('itemtype');
        });
    }

    updateHighlightReviewDescription() {
        const reviewList = document.querySelector('.bv-content-list.bv-content-list-reviews');
        const highlightDescription = document.querySelector('.highlight-review-description');
        const firstReview = reviewList?.querySelector('li.bv-content-item');
        const reviewBodyText = firstReview?.querySelector('.bv-content-summary-body-text');

        if (!reviewList || !highlightDescription || !reviewBodyText) {
            return;
        }

        const reviewTextContent = reviewBodyText.textContent.trim();
        const maxLength = 350;

        if (reviewTextContent.length > maxLength) {
            const shortText = reviewTextContent.substring(0, maxLength) + '...';
            highlightDescription.innerHTML = `<span class="highlight-review-short">${shortText}</span>
                                              <span class="highlight-review-full" style="display:none;">${reviewTextContent}</span>
                                              <a href="#" class="highlight-review-read-more" data-fulltext="${reviewTextContent}">Read more</a>`;
        } else {
            highlightDescription.textContent = reviewTextContent;
        }
    }

    readMoreHandler() {
        document.addEventListener("click", function (event) {
            if (event.target.classList.contains("highlight-review-read-more")) {
                event.preventDefault();

                const readMoreLink = event.target;
                const shortTextElement = readMoreLink.previousElementSibling.previousElementSibling;
                const fullTextElement = readMoreLink.previousElementSibling;

                if (shortTextElement && fullTextElement) {
                    shortTextElement.style.display = "none";
                    fullTextElement.style.display = "inline";
                    readMoreLink.style.display = "none";
                }
            }
        });
    }

    reviewsChartClone() {
        const bvReviewChart = '.bv-section-summary';
        const reviewChart = document.querySelector(bvReviewChart);

        if (reviewChart) {
            const bvReviewsHighlightChart = document.querySelector('.bv-review-highlight-chart');
            const summary = document.querySelector('.bv-section-summary');
            const clonedChart = reviewChart.cloneNode(true);

            this.copyReviewsChartComputedStyles(reviewChart, clonedChart);

            if (window.innerWidth <= 768) {
                summary.style.width = '100%';
            }

            if (bvReviewsHighlightChart) {
                bvReviewsHighlightChart.appendChild(clonedChart);
            }
        }
    }

    reviewHighlightCountModifier() {
        const containers = document.querySelectorAll('.bv_numReviews_component_container');
        if (containers.length > 0) {
            containers.forEach((container) => {
                const reviewsTextElement = container.querySelector('.bv_numReviews_text');

                if (reviewsTextElement) {
                    const reviewCountMatch = reviewsTextElement.textContent.match(/\d+/);

                    if (reviewCountMatch) {
                        const reviewCount = parseInt(reviewCountMatch[0], 10);

                        const newReviewCountElement = document.createElement('div');
                        newReviewCountElement.className = 'bv_numReviews_injected_text';

                        const reviewText = reviewCount === 1 ? 'review' : 'reviews';
                        newReviewCountElement.textContent = `(${reviewCount} ${reviewText})`;

                        container.appendChild(newReviewCountElement);
                    }

                    reviewsTextElement.remove();
                }
            });
        }
    }

    individualRatingNumber() {
        var ratingWrapper = document.querySelector('.b-product-rating-wrapper');
        if (ratingWrapper) {
            var offScreenElement = ratingWrapper.querySelector('.bv-off-screen');

            if (offScreenElement) {
                var offScreenText = offScreenElement.textContent.trim();
                var match = offScreenText.match(/(\d+)\s+review[s]?/);

                if (match) {
                    var numberOfReviews = match[1];
                    var reviewCountText = document.createTextNode(' (' + numberOfReviews + ')');
                    ratingWrapper.appendChild(reviewCountText);
                    ratingWrapper.style.display = 'flex';
                }
            }
        }
    }

    filterModifier() {
        const button = document.querySelector('.bv-content-btn.bv-filter-control.bv-expand-filter-button');
        if (button) {
            const span = button.querySelector('.bv-filter-control-text');
            if (span) {
                span.textContent = 'Filter by +';
                button.addEventListener('click', function () {
                    span.textContent = span.textContent === "Filter by +" ? "Filter by -" : "Filter by +";
                });
            }
        }

        const dropdownLabel = document.querySelector('#bv-dropdown-select-reviews');
        if (dropdownLabel) {
            dropdownLabel.innerHTML = 'Sort by <span class="chevron">▼</span>';
        }
    }

    chartRateNumberModifier() {
        var histogramBars = document.querySelectorAll('[data-bv-histogram-rating-count]');

        for (var i = 0; i < histogramBars.length; i++) {
            var bar = histogramBars[i];
            var ratingCount = bar.getAttribute('data-bv-histogram-rating-count');
            var scoreElement = bar.nextElementSibling;

            if (scoreElement && scoreElement.classList.contains('bv-inline-histogram-ratings-score')) {
                var scoreSpan = scoreElement.querySelector('span[aria-hidden="true"]');
                if (scoreSpan) {
                    scoreSpan.textContent = '(' + ratingCount + ')';
                }
            }
        }
    }

    reviewAuthorModifier() {
        var metaWrappers = document.querySelectorAll('.bv-content-meta-wrapper');

        metaWrappers.forEach(wrapper => {
            if (!wrapper.dataset.processed) {
                wrapper.innerText = wrapper.innerText.replace(/\s·/g, ',');
                wrapper.dataset.processed = 'true';
            }
        });
    }

    noReviewsTextInjection() {
        const firstToWriteElement = document.getElementById('first-to-write');
        if (firstToWriteElement) {
            firstToWriteElement.textContent = 'Be the first to review';
        }
    }

    handleLoadMore() {
        var self = this;
        loadMoreButton.addEventListener('click', function () {
            setTimeout(function () {
                self.paginationModifier();
                self.updateRatings();
                this.individualRatingNumber();
            }, 500);
        });
    }

    updateRatings() {
        var ratingContainers = document.querySelectorAll('.bv-content-rating.bv-rating-ratio');
        for (var i = 0; i < ratingContainers.length; i++) {
            const ratingValueElement = ratingContainers[i].querySelector('meta[content]');

            if (ratingValueElement) {
                const ratingValue = ratingValueElement.getAttribute('content');

                if (ratingValue && !ratingContainers[i].dataset.ratingApplied) {
                    ratingContainers[i].innerHTML += ratingValue;
                    ratingContainers[i].dataset.ratingApplied = 'true';
                }
            }
        }
    }

    updateActionBarHeader() {
        const actionBarHeader = document.querySelector('.bv-action-bar-header');
        if (actionBarHeader) {
            actionBarHeader.textContent = 'Customer Reviews';
        }
    }

    handleModalVisibility() {
        var modalContainer = document.querySelector('.bv-reviews-container-modal');
        if (modalContainer) {
            var isModalOpen = modalContainer.classList.contains('show');
            if (isModalOpen) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        }
    }

    adjustModalHeight() {
        var modalContainer = document.querySelector('.bv-reviews-container-modal');

        if (modalContainer) {
            var headerContent = document.querySelector('.l-header__content');
            var headerHeight = headerContent ? headerContent.offsetHeight : 0;
            var viewportHeight = window.innerHeight;
            var modalHeight = viewportHeight - headerHeight;

            if (window.innerWidth <= 376) {
                modalContainer.style.height = `${modalHeight}px`;
                headerContent.style.backgroundColor = 'rgb(252, 249, 246)';
            } else {
                modalContainer.style.height = '100%';
            }
        }
    }

    copyComputedStyles(sourceElement, targetElement) {
        if (!sourceElement || !targetElement) {
            return;
        }

        var computedStyle = window.getComputedStyle(sourceElement);
        var targetStyle = targetElement.style;

        for (var i = 0; i < computedStyle.length; i++) {
            var property = computedStyle[i];
            targetStyle.setProperty(property, computedStyle.getPropertyValue(property));
        }

        var sourceChildren = sourceElement.children;
        var targetChildren = targetElement.children;

        for (var j = 0; j < sourceChildren.length; j++) {
            if (targetChildren[j]) {
                this.copyComputedStyles(sourceChildren[j], targetChildren[j]);
            }
        }
    }

    copyReviewsChartComputedStyles(source, target) {
        if (!(source instanceof Element) || !(target instanceof Element)) {
            return;
        }

        const computedStyles = window.getComputedStyle(source);
        for (const property of computedStyles) {
            target.style.setProperty(property, computedStyles.getPropertyValue(property));
        }

        const sourceChildren = source.children;
        const targetChildren = target.children;

        const minLength = Math.min(sourceChildren.length, targetChildren.length);

        for (let i = 0; i < minLength; i++) {
            this.copyReviewsChartComputedStyles(sourceChildren[i], targetChildren[i]);
        }
    }

    cloneReviewHeaderMeta() {
        const highlightAuthorTime = document.querySelector('.highlight-rating-author-time');
        const reviewList = document.querySelector('.bv-content-list.bv-content-list-reviews');
        const firstReview = reviewList?.querySelector('li.bv-content-item');

        const ratingStars = firstReview?.querySelector('.bv-content-rating.bv-rating-ratio');
        if (ratingStars) {
            const clonedRatingStars = ratingStars.cloneNode(true);
            this.copyComputedStyles(ratingStars, clonedRatingStars);
        }

        const metaWrapper = firstReview?.querySelector('.bv-content-meta-wrapper');
        if (metaWrapper && highlightAuthorTime) {
            const clonedMetaWrapper = metaWrapper.cloneNode(true);
            this.copyComputedStyles(metaWrapper, clonedMetaWrapper);
            clonedMetaWrapper.innerHTML = clonedMetaWrapper.textContent.replace(/\s+/g, ' ').trim();
            highlightAuthorTime.appendChild(clonedMetaWrapper);
        }
    }

    cloneReviewContentData() {
        const reviewList = document.querySelector('.bv-content-list.bv-content-list-reviews');
        const boughtContainer = document.querySelector('.bv-review-bought');
        const firstReview = reviewList?.querySelector('li.bv-content-item');

        if (!reviewList) {
            return;
        }

        const contentData = firstReview?.querySelector('.bv-content-data');

        const clonedContentData = contentData?.cloneNode(true);
        this.copyComputedStyles(contentData, clonedContentData);

        if (boughtContainer) {
            boughtContainer.appendChild(clonedContentData);
        }
    }

    removeMicrodataAttributes() {
        const container = document.getElementById('BVRRContainer');
        if (container) {
            const elements = container.querySelectorAll('[itemprop], [itemscope], [itemtype]');
            elements.forEach(element => {
                element.removeAttribute('itemprop');
                element.removeAttribute('itemscope');
                element.removeAttribute('itemtype');
            });
        }
    }

    showReviewsModal() {
        const reviewsModalEl = document.querySelector('.bv-reviews-container-modal');
        reviewsModalEl?.classList.add('show');
    }

    closeReviewsModal() {
        const reviewsModalEl = document.querySelector('.bv-reviews-container-modal');
        reviewsModalEl?.classList.remove('show');
    }
}